/**
 * ShareStudyDialog — Allows sharing a study with another user via message.
 * Also shows existing shares for a single study.
 *
 * Calls api.shareStudy(studyKey, to, message) and api.getSharingBeans(studyKey).
 */
import { useState, useEffect } from 'react';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Alert,
  CircularProgress,
  Typography,
  Box,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { api } from '../../api/rpc';
import type { Study, User, SharingBean } from '../../api/types';

interface ShareStudyDialogProps {
  open: boolean;
  studies: Study[];
  onClose: () => void;
  onComplete?: () => void;
}

export const ShareStudyDialog = ({
  open,
  studies,
  onClose,
  onComplete,
}: ShareStudyDialogProps) => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [existingShares, setExistingShares] = useState<SharingBean[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);

  // Load users when dialog opens
  useEffect(() => {
    if (!open) return;
    setError(null);
    setSelectedUser(null);
    setMessage('');
    setLoadingUsers(true);
    api
      .getUsersPaged(0, 500)
      .then((r) => setUsers(r.records ?? []))
      .catch((e) => setError(e.message))
      .finally(() => setLoadingUsers(false));

    // Load existing shares only when a single study is selected
    if (studies.length === 1 && studies[0].objKey) {
      setLoadingShares(true);
      api
        .getSharingBeans(studies[0].objKey)
        .then((beans) => setExistingShares(beans ?? []))
        .catch(() => setExistingShares([]))
        .finally(() => setLoadingShares(false));
    } else {
      setExistingShares([]);
    }
  }, [open, studies]);

  const handleShare = async () => {
    if (!selectedUser) return;
    setPending(true);
    setError(null);
    try {
      for (const s of studies) {
        await api.shareStudy(s.objKey, selectedUser.objKey, message);
      }
      onComplete?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Share Study</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {studies.length === 1 && (
          <Box>
            <Typography variant="subtitle2" sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
              Existing Shares {loadingShares ? '…' : `(${existingShares.length})`}
            </Typography>
            {!loadingShares && existingShares.length === 0 && (
              <Typography sx={{ fontSize: 11, color: 'text.secondary', fontStyle: 'italic' }}>
                No existing shares.
              </Typography>
            )}
            {existingShares.length > 0 && (
              <Table size="small" sx={{ '& td': { fontSize: 11, py: 0.25 } }}>
                <TableBody>
                  {existingShares.map((s) => (
                    <TableRow key={s.objKey}>
                      <TableCell>{s.to ?? '—'}</TableCell>
                      <TableCell>{s.action ?? '—'}</TableCell>
                      <TableCell>{s.expires ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <Divider sx={{ mt: 1 }} />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        <Autocomplete
          options={users}
          getOptionLabel={(u) => `${u.name ?? u.user} (${u.user})`}
          loading={loadingUsers}
          value={selectedUser}
          onChange={(_e, v) => setSelectedUser(v)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Share with"
              placeholder="Select a user…"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingUsers ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <TextField
          label="Message (optional)"
          multiline
          minRows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleShare}
          disabled={pending || !selectedUser}
        >
          {pending ? 'Sharing…' : 'Share'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
